import { Alert, Anchor, Badge, Group, Paper, SegmentedControl, Stack, Text } from '@mantine/core';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import { listKey } from '../../api/queryKeys';
import { EmptyNote } from '../../components/RecordScreen';
import { formatDate, formatMoney, formatQty, formatRateBp, formatRelative } from '../../lib/format';

const PERFORMANCE_PATH = '/admin/v1/suppliers/supplier-performance';

interface ShortLine {
  purchaseOrderId: string;
  inventoryItemId: string;
  itemName: string | null;
  unit: string;
  orderedQty: number;
  receivedQty: number;
  shortQty: number;
  orderedAt: string | null;
  lastArrivedAt: string | null;
}

interface RepricedLine {
  purchaseOrderId: string;
  inventoryItemId: string;
  itemName: string | null;
  currency: string;
  orderedPriceMinor: number;
  chargedPriceMinor: number;
  changeBp: number;
  arrivedAt: string;
}

export interface Scorecard {
  supplierId: string;
  supplierName: string;
  deliveries: number;
  deliveriesAgainstAnOrder: number;
  lastDeliveredAt: string | null;
  linesJudged: number;
  linesFilledInFull: number;
  fillRateBp: number | null;
  whyNoRate: string | null;
  shortLines: ShortLine[];
  ordersStillOut: number;
  repricedLines: RepricedLine[];
  repricedUpCount: number;
  billsThatDidNotAgree: number;
  billsStillUnanswered: number;
}

interface Performance {
  sinceDate: string;
  enoughDeliveriesToRate: number;
  enoughLinesToRate: number;
  suppliers: Scorecard[];
}

const PERIODS = [
  { value: '90', label: 'Last 90 days' },
  { value: '180', label: 'Last 6 months' },
  { value: '365', label: 'Last year' },
];

export function useScorecards(days: string, supplierId?: string) {
  const query = new URLSearchParams({ days });
  if (supplierId) query.set('supplierId', supplierId);
  return useQuery({
    queryKey: listKey(PERFORMANCE_PATH, { days, supplierId }),
    queryFn: () => rawApi<Performance>('GET', `${PERFORMANCE_PATH}?${query.toString()}`),
    staleTime: 60_000,
  });
}

/** Green above nine in ten, amber above four in five, red below. Nothing prints a colour on a null. */
function rateColour(bp: number): string {
  if (bp >= 9_000) return 'green';
  if (bp >= 8_000) return 'yellow';
  return 'red';
}

/**
 * Who actually delivers what they promise.
 *
 * The screen is built around one refusal, and the refusal is the feature: a vendor with two
 * deliveries gets no percentage. "50%" off one short line out of two would be acted on — he would
 * move the produce order — and there is nothing behind it. So the badge is replaced by the server's
 * own sentence about why, and the shortfalls themselves are listed underneath either way, because
 * "they were three cases short of rum on the 14th" is a fact at any sample size and it is the one
 * he can raise with them.
 */
export function SupplierScorecardSection() {
  const [days, setDays] = useState('180');
  const performance = useScorecards(days);
  const data = performance.data;

  return (
    <>
      <Group justify="space-between" align="flex-end" mb="sm">
        <div>
          <Text fw={600}>How they do</Text>
          <Text size="xs" c="dimmed">
            What you ordered against what turned up, from{' '}
            {data ? formatDate(data.sinceDate) : 'the period below'}. Worst first.
          </Text>
        </div>
        <SegmentedControl size="xs" data={PERIODS} value={days} onChange={setDays} />
      </Group>

      {performance.isLoading ? (
        <EmptyNote>Loading…</EmptyNote>
      ) : performance.isError ? (
        <EmptyNote>This could not be loaded. Nothing has been lost — try again in a moment.</EmptyNote>
      ) : !data || data.suppliers.length === 0 ? (
        <EmptyNote>
          Nothing has been delivered in this period, so there is nothing to judge anyone on yet.
        </EmptyNote>
      ) : (
        <Stack gap="sm">
          {data.suppliers.map((card) => (
            <ScorecardCard key={card.supplierId} card={card} />
          ))}
        </Stack>
      )}
    </>
  );
}

export function ScorecardCard({ card, hideName }: { card: Scorecard; hideName?: boolean }) {
  const short = card.shortLines;
  const repriced = card.repricedLines;

  return (
    <Paper withBorder p="md" radius="sm">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <div style={{ minWidth: 0 }}>
          {!hideName && (
            <Anchor component={Link} to={`/suppliers/${card.supplierId}`} fw={600}>
              {card.supplierName}
            </Anchor>
          )}
          <Text size="xs" c="dimmed">
            {card.deliveries} {card.deliveries === 1 ? 'delivery' : 'deliveries'}
            {card.deliveriesAgainstAnOrder !== card.deliveries
              ? ` · ${card.deliveriesAgainstAnOrder} against an order`
              : ''}
            {card.lastDeliveredAt ? ` · last ${formatRelative(card.lastDeliveredAt)}` : ''}
            {card.ordersStillOut > 0
              ? ` · ${card.ordersStillOut} ${card.ordersStillOut === 1 ? 'order' : 'orders'} still out`
              : ''}
          </Text>
        </div>
        {/* The whole design of this screen is in these three lines: a figure when there is one,
            and the reason there is not when there is not. Never a percentage over thin data. */}
        {card.fillRateBp === null ? (
          <Badge variant="light" color="gray" style={{ flexShrink: 0 }}>
            Too early to say
          </Badge>
        ) : (
          <Badge variant="light" color={rateColour(card.fillRateBp)} size="lg" style={{ flexShrink: 0 }}>
            {formatRateBp(card.fillRateBp)} arrived in full
          </Badge>
        )}
      </Group>

      {card.whyNoRate && (
        <Text size="xs" c="dimmed" mt={6} fs="italic">
          {card.whyNoRate}
        </Text>
      )}
      {card.fillRateBp !== null && (
        <Text size="xs" c="dimmed" mt={6}>
          {card.linesFilledInFull} of {card.linesJudged} things you ordered came in full.
        </Text>
      )}

      {short.length > 0 && (
        <Stack gap={2} mt="sm">
          <Text size="sm" fw={500}>
            What they were short on
          </Text>
          {short.slice(0, 5).map((line) => (
            <Text key={`${line.purchaseOrderId}-${line.inventoryItemId}`} size="sm">
              {/* Not a link to the order: orders open in a modal from their own list, and a link
                  to a route that does not exist is worse than plain text. */}
              <strong>{line.itemName ?? 'Something no longer on file'}</strong>{' '}
              <Text span c="dimmed">
                — {formatQty(line.shortQty, line.unit)} short of the {formatQty(line.orderedQty, line.unit)}{' '}
                ordered
                {line.lastArrivedAt ? `, ${formatRelative(line.lastArrivedAt)}` : ''}
              </Text>
            </Text>
          ))}
          {short.length > 5 && (
            <Text size="xs" c="dimmed">
              and {short.length - 5} more.
            </Text>
          )}
        </Stack>
      )}

      {repriced.length > 0 && (
        <Alert color="orange" variant="light" mt="sm" title="They charged more than the order said">
          <Stack gap={2}>
            {repriced.slice(0, 4).map((line) => (
              <Text key={`${line.purchaseOrderId}-${line.inventoryItemId}-${line.arrivedAt}`} size="sm">
                {line.itemName ?? 'Something no longer on file'} — ordered at{' '}
                {formatMoney(line.orderedPriceMinor, line.currency)}, invoiced at{' '}
                {formatMoney(line.chargedPriceMinor, line.currency)} ({line.changeBp > 0 ? '+' : ''}
                {formatRateBp(line.changeBp)}) on {formatDate(line.arrivedAt)}
              </Text>
            ))}
            {repriced.length > 4 && (
              <Text size="xs" c="dimmed">
                and {repriced.length - 4} more.
              </Text>
            )}
          </Stack>
        </Alert>
      )}

      {card.billsThatDidNotAgree > 0 && (
        <Text size="xs" c={card.billsStillUnanswered > 0 ? 'orange.8' : 'dimmed'} mt="sm">
          {card.billsThatDidNotAgree}{' '}
          {card.billsThatDidNotAgree === 1 ? 'bill did not agree' : 'bills did not agree'} with the goods
          behind {card.billsThatDidNotAgree === 1 ? 'it' : 'them'}
          {card.billsStillUnanswered > 0 ? ` · ${card.billsStillUnanswered} still unanswered` : ''}.
        </Text>
      )}
    </Paper>
  );
}
